import React from 'react';
import { useEditor, Editor } from '@tiptap/react';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Document from '@tiptap/extension-document';
import History from '@tiptap/extension-history';
import Placeholder from '@tiptap/extension-placeholder';
import { ReferenceTransformer } from '../extensions/ReferenceTransformerExtension';
import { Reference } from '../extensions/ReferenceExtension';
import { REFERENCE_DOUBLE_MUSTACHE_PATTERN } from '../regex';
import { processAllReferences } from '../utils/referenceTransformUtils';

interface UseTipTapFormFieldProps {
  readOnly?: boolean;
  multiline?: boolean;
  initialContent: string | undefined | object;
  onChange: (output: string) => Promise<void>;
  onFocus?: () => void;
  onBlur?: (event?: any) => void;
  placeholder?: string;
}

/**
 * Lightweight hook for TipTap form field editor with reference decoration support
 * Optimized for simple text inputs that may contain references
 */
export const useTipTapFormField = ({
  readOnly = false,
  placeholder,
  initialContent = '',
  onChange,
  onFocus,
  onBlur,
}: UseTipTapFormFieldProps) => {
  const editorUpdateTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const editorRef = React.useRef<Editor | null>(null);

  // Function to insert a reference at the current cursor position
  const insertReferenceAtCursor = React.useCallback((referenceId: string) => {
    if (!editorRef.current) return;

    // Basic cleanup - just make sure there are no double braces
    const cleanId = referenceId.replace(/^\{\{|\}\}$/g, '');

    // Check if it has a resource prefix (inputs. or steps.)
    const hasResourcePrefix = cleanId.match(/^(inputs|steps)\./);

    // If no resource prefix, add a default one (inputs.)
    const finalId = hasResourcePrefix ? cleanId : `inputs.${cleanId}`;

    // Insert the reference followed by a space for better cursor positioning
    const result = editorRef.current
      .chain()
      .focus()
      .insertContent([
        {
          type: 'reference',
          attrs: {
            id: finalId,
          },
        },
        {
          type: 'text',
          text: ' ', // Add a space after the reference
        },
      ])
      .run();

    // Fix cursor position and ensure references are properly decorated
    setTimeout(() => {
      if (editorRef.current) {
        processAllReferences(editorRef.current);
        editorRef.current.commands.focus(); // Ensure editor keeps focus
      }
    }, 50);
  }, []);

  // Format content for database storage - extract only text with {{reference}} patterns
  // const formatContentForStorage = React.useCallback(
  //   (editor: Editor | null): string => {
  //     if (!editor) return '';

  //     try {
  //       // Extract references directly from HTML
  //       const html = editor.getHTML();

  //       // Create a temporary div to parse HTML
  //       const tempDiv = document.createElement('div');
  //       tempDiv.innerHTML = html;

  //       // Get all reference spans
  //       const referenceSpans = tempDiv.querySelectorAll(
  //         'span[data-reference][data-id]'
  //       );

  //       // If no references found, just return the text
  //       if (referenceSpans.length === 0) {
  //         return editor.getText();
  //       }

  //       // Replace reference spans with {{id}} format in the HTML
  //       referenceSpans.forEach((span) => {
  //         const id = span.getAttribute('data-id');
  //         if (id) {
  //           // Format with braces
  //           const formattedRef = normalizeReferenceId(id, true);
  //           const refText = document.createTextNode(formattedRef);
  //           span.parentNode?.replaceChild(refText, span);
  //         }
  //       });

  //       // Get text content from the cleaned HTML
  //       const plainText = tempDiv.textContent || '';
  //       console.log('plainText', plainText);
  //       // Clean up any special characters or formats we don't want
  //       // Preserve line breaks if multiline is enabled, otherwise collapse to spaces
  //       return plainText.replace(/\n+/g, multiline ? '\n' : ' ').trim();
  //     } catch (error) {
  //       // Fall back to simple text extraction if there's an error

  //       console.log('plainText error', editor.getText());
  //       return editor.getText() || '';
  //     }
  //   },
  //   [multiline]
  // );

  // Initialize the editor with minimal extensions for form field use
  const editor = useEditor(
    {
      extensions: [
        // Use only the minimum required extensions
        Document,
        Paragraph,
        Text,
        History,
        Placeholder.configure({
          placeholder: placeholder || 'Type here...',
        }),
        Reference,
        ReferenceTransformer,
      ],
      content: initialContent,
      editable: !readOnly,
      onCreate: ({ editor }) => {
        // Store the editor instance for later reference
        editorRef.current = editor;
      },
      onUpdate: ({ editor }) => {
        console.log('onUpdate', editor.getText());
        // Debounce the onChange to prevent racing conditions
        if (editorUpdateTimeout.current) {
          clearTimeout(editorUpdateTimeout.current);
        }

        editorUpdateTimeout.current = setTimeout(() => {
          const doc = editor.state.doc;
          let output = '';

          doc.forEach((node, offset, index) => {
            node.forEach((child) => {
              if (child.type.name === 'reference') {
                const refId = child.attrs.id;
                output += `{{${refId}}}`;
              } else {
                output += child.text || '';
              }
            });
            output += '\n';
          });

          onChange(output.trim());
        }, 300); // Shorter timeout for form fields
      },
      onFocus: () => {
        if (onFocus) {
          onFocus();
        }
      },
      onBlur: (event) => {
        if (onBlur) {
          onBlur(event);
        }
      },
    },
    []
  );

  // Process content when initialContent changes from outside
  React.useEffect(() => {
    if (!editor || initialContent === undefined) return;

    try {
      // If not focused (to avoid disrupting user editing)
      if (!editor.isFocused) {
        // Handle string content
        if (typeof initialContent === 'string') {
          // Check if it has any {{references}}
          if (initialContent.includes('{{') && initialContent.includes('}}')) {
            // Split content by line breaks to create multiple paragraphs
            const lines = initialContent.split(/\n/g);
            let htmlContent = '';

            lines.forEach((line) => {
              htmlContent += '<p>';

              // Split by references - match {{type.id}} patterns
              const parts = line.split(/(\{\{[^}]+\}\})/g);
              parts.forEach((part) => {
                const refMatch = part.match(/^\{\{([^}]+)\}\}$/);
                if (refMatch) {
                  // This is a reference - extract the ID (e.g., "inputs.someId")
                  const refId = refMatch[1];
                  // Create a reference span that the Reference extension can recognize
                  htmlContent += `<span data-reference data-id="${refId}"></span>`;
                } else if (part) {
                  // This is plain text - encode it to prevent HTML issues
                  htmlContent += part
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#039;');
                }
              });

              htmlContent += '</p>';
            });

            // Set the HTML content
            editor.commands.setContent(htmlContent);

            // Process references to ensure proper decoration
            processAllReferences(editor);
          } else {
            // Plain text without references - split into multiple paragraphs
            const lines = initialContent.split(/\n/g);
            const htmlContent = lines.map((line) => `<p>${line}</p>`).join('');

            editor.commands.setContent(htmlContent);
          }
        } else {
          // If we got an object, just set it as content
          editor.commands.setContent(initialContent);
        }
      }
    } catch (error) {
      // Fallback to setting content directly as plain text
      try {
        // Even in fallback, try to respect line breaks
        const lines = String(initialContent).split(/\n/g);
        const htmlContent = lines.map((line) => `<p>${line}</p>`).join('');

        editor.commands.setContent(htmlContent);
      } catch (e) {
        // Unable to set content, fail silently
      }
    }
  }, [initialContent, editor]);

  // Process references in the content
  const decorateReferences = React.useCallback(() => {
    if (editor) {
      processAllReferences(editor);
    }
  }, [editor]);

  // Add fallback reference transformation
  React.useEffect(() => {
    if (!editor || readOnly) return;

    let isMounted = true;

    try {
      if (!isMounted || !editor) return;

      // Get current HTML content
      const content = editor.getHTML();

      // Check if there are still references in the content after automatic transformation
      const matches = content.match(REFERENCE_DOUBLE_MUSTACHE_PATTERN);

      if (matches && matches.length > 0) {
        decorateReferences();
      }
    } catch (error) {
      // Error checking for untransformed references, fail silently
    }

    return () => {
      isMounted = false;

      // Clean up any pending timers
      if (editorUpdateTimeout.current) {
        clearTimeout(editorUpdateTimeout.current);
      }
    };
  }, [editor, readOnly, decorateReferences]);

  return {
    editor,
    editorRef,
    decorateReferences,
    insertReferenceAtCursor,
  };
};
